import React, { useEffect, useRef } from 'react';
import { Dialog, DialogFooter } from '../Dialog';
import { Button, ButtonVariant } from '../Buttons';
import logoutIllustrationUrl from '../../../assets/svg/logout-illustration.svg?url';

export interface LogoutDialogProps {
  /**
   * Whether the dialog is open
   */
  open: boolean;
  /**
   * Callback when the dialog should be closed
   */
  onClose: () => void;
  /**
   * Callback when logout is confirmed
   */
  onConfirm: () => void;
  /**
   * Whether logout is in progress
   */
  isLoggingOut?: boolean;
}

/**
 * LogoutDialog Component
 * Displays a confirmation dialog for logout action
 */
export const LogoutDialog: React.FC<LogoutDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isLoggingOut = false,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  const handleCancel = () => {
    if (!isLoggingOut) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (!isLoggingOut) {
      onConfirm();
    }
  };

  // Focus management: Set initial focus on Cancel button when dialog opens
  useEffect(() => {
    if (open && dialogRef.current && !isLoggingOut) {
      // Small delay to ensure dialog is fully rendered
      const timer = setTimeout(() => {
        // Find Cancel button by test id to avoid label localization issues
        const cancelButton =
          dialogRef.current?.querySelector<HTMLButtonElement>(
            '[data-testid="logout-dialog-cancel"]'
          );
        if (cancelButton) {
          cancelButton.focus();
        }
      }, 100);
      return () => {
        clearTimeout(timer);
      };
    }
    return undefined;
  }, [open, isLoggingOut]);

  // Focus trapping: Keep focus within dialog buttons
  useEffect(() => {
    if (!open || !dialogRef.current) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') {
        return;
      }

      // Get focusable buttons within the dialog
      const buttons = Array.from(
        dialogRef.current?.querySelectorAll<HTMLButtonElement>(
          'button:not([disabled])'
        ) || []
      );

      if (buttons.length === 0) {
        return;
      }

      const firstButton = buttons[0];
      const lastButton = buttons[buttons.length - 1];
      const activeElement = document.activeElement as HTMLElement;

      if (!firstButton || !lastButton) {
        return;
      }

      if (e.shiftKey) {
        // Shift+Tab: Move backwards
        if (activeElement === firstButton) {
          e.preventDefault();
          lastButton.focus();
        }
      } else {
        // Tab: Move forwards
        if (activeElement === lastButton) {
          e.preventDefault();
          firstButton.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      closeOnOverlayClick={!isLoggingOut}
      closeOnEscape={!isLoggingOut}
      maxWidth={432}
      className="bg-white"
      data-testid="logout-dialog"
    >
      <div
        ref={dialogRef}
        className="flex flex-col items-center gap-6 p-[24px]"
        data-testid="logout-dialog-content"
      >
        {/* Illustration */}
        <div className="flex items-center justify-center w-[137px] h-[137px]">
          <img
            src={logoutIllustrationUrl}
            alt="Logout illustration"
            className="w-full h-full"
            data-testid="logout-illustration"
          />
        </div>

        {/* Content */}
        <div className="flex flex-col items-center gap-4 w-full">
          {/* Title - Log out? */}
          <h2
            className="text-center"
            data-testid="logout-dialog-title"
            style={{
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: 18,
              lineHeight: '26px',
              letterSpacing: '0px',
              color: '#474A4C',
              width: '332px',
              height: '26px',
              margin: 0,
            }}
          >
            Wylogować się?
          </h2>
          {/* Description */}
          <p
            className="text-center"
            data-testid="logout-dialog-description"
            style={{
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.25px',
              color: '#474A4C',
              width: '332px',
              margin: 0,
            }}
          >
            Czy na pewno chcesz się wylogować z tej aplikacji?
          </p>
        </div>

        {/* Actions */}
        <DialogFooter className="w-full justify-center !py-1">
          <div className="flex w-full justify-center" style={{ gap: '12px' }}>
            <Button
              label="Anuluj"
              onClick={handleCancel}
              disabled={isLoggingOut}
              data-testid="logout-dialog-cancel"
              className={`!h-[40px] !rounded-[8px] !text-[14px] !font-medium !leading-[20px] !tracking-[0.1px] w-full ${
                isLoggingOut
                  ? '!bg-[#181A1B1A] !border-[#DCDDE0] opacity-38 !text-[#181A1B] !cursor-not-allowed'
                  : '!bg-transparent !border !border-[#DCDDE0] !text-[#474A4C] hover:!bg-neutral-95 cursor-pointer'
              }`}
              style={{
                border: isLoggingOut
                  ? '1px solid #DCDDE0'
                  : '1px solid #DCDDE0',
              }}
            />
            <Button
              label={isLoggingOut ? 'Wylogowywanie…' : 'Wyloguj'}
              variant={ButtonVariant.Error}
              onClick={handleConfirm}
              disabled={isLoggingOut}
              data-testid="logout-dialog-confirm"
              className="!h-[40px] !rounded-[8px] !text-[14px] !font-medium !leading-[20px] !tracking-[0.1px] !text-white w-full cursor-pointer"
            />
          </div>
        </DialogFooter>
      </div>
    </Dialog>
  );
};

export default LogoutDialog;
